<template>
  <div class="d-flex flex-column mt-6 mb-6 text-center"
       id="contact-form">
    <h1 class="my-4">Ready for a Quote? Tell Us About Your Project First</h1>
    <form ref="tellUsAboutYourProject"
          enctype="multipart/form-data"
          method="post"
          :valid="isValid"
          @submit.prevent="sendMail()">
      <v-stepper v-model="step"
                 show-actions
                 :height="isCarouselActive ? 'auto' : '780px'"
                 class="rounded-xl"
                 :mobile="display.mobile"
                 mobile-breakpoint="sm"
                 alt-labels>
        <template v-slot:default>
          <v-stepper-header error-color="red">
            <template v-for="item, index in items"
                      :key="`${item}-step`">
              <v-stepper-item :complete="step > index"
                              :title="display.mobile ? '' : item"
                              :step="index"
                              :rules="[() => stepValidation(item)]"
                              :value="index"
                              editable>
                <template v-slot:icon>
                  <v-icon>
                    {{ getStepIcon(index) }}
                  </v-icon>
                </template>
                <template v-slot:subtitle="{ hasError }">
                  {{ hasError ? 'Missing Information' : '' }}
                </template>
              </v-stepper-item>

              <v-divider v-if="step !== index"
                         :key="item"></v-divider>
            </template>
          </v-stepper-header>

          <v-stepper-window>
            <!-- Step 1: About You -->
            <v-stepper-window-item :value="0"
                                   :key="`${items[0]}-content`">
              <CustomerDetails :model-value="customerProject"></CustomerDetails>
            </v-stepper-window-item>

            <!-- Step 2: About Your Project -->
            <v-stepper-window-item :value="1"
                                   :key="`${items[1]}-content`">
              <ProjectDetails :model-value="customerProject"></ProjectDetails>
            </v-stepper-window-item>
          </v-stepper-window>

        </template>
        <template v-slot:actions>
          <div class="d-flex w-100 pa-6 justify-space-between">
            <v-btn :color="step === 0 ? 'black' : 'secondary'"
                   :disabled="step === 0"
                   :variant="step === 0 ? 'text' : 'elevated'"
                   @click="step--">
              Back
            </v-btn>
            <v-btn v-if="step < items.length - 1"
                   color="primary"
                   @click="step++">
              Next
            </v-btn>
            <v-btn v-if="step === items.length - 1"
                   color="primary"
                   type="submit"
                   @click.prevent="sendMail()">
              Submit
            </v-btn>
          </div>
        </template>
      </v-stepper>
    </form>
    <v-snackbar v-model="snackbar"
                :color="isValid ? 'primary' : 'secondary'"
                :timeout="8000"
                multi-line
                vertical
                transition="slide-x-transition">
      <template v-slot:default>
        {{ isValid ? 'Thank you for submitting your request! We’ve received your details and will be in touch shortly.'
          : 'Oops, something went wrong.' }}
      </template>
      <template v-slot:actions>
        <v-btn color="secondary"
               variant="text"
               @click="snackbar = false">
          Close
        </v-btn>
      </template>
    </v-snackbar>
  </div>
</template>

<script lang="ts" setup>
import { ref } from 'vue';
import { ContactMethod, type ContactModel } from '~/models/contactModel';
import { reactive } from 'vue';
import { useDisplay } from 'vuetify';
import { useEmailJS } from '~/composables/useEmailJS';

const display = ref(useDisplay());
const tellUsAboutYourProject = ref<HTMLFormElement>();
const isValid = ref(true);
const mail = useEmailJS();
const snackbar = ref(false);
const step = ref(0);
const items = ["About You", "Your Project"];

const getStepIcon = (index: number): string => {
  if (index === 0) return 'mdi-account';
  if (index === 1) return 'mdi-briefcase';
  return 'mdi-check-circle';
};

const isCarouselActive = computed(() => {
  return step.value == 1 && customerProject.project.images.length > 2;
});
const customerProject = reactive<ContactModel>({
  customer: {
    name: '',
    address: '',
    city: '',
    state: '',
    zip: '',
    email: '',
    phone: '',
  },
  project: {
    description: '',
    images: [],
  },
  preferredContact: ContactMethod.Email,
});

function validateStepRules(index: number): boolean {
  if (index === 1) {
    return stepValidation(items[0]);
  } else if (index === 2) {
    return stepValidation(items[1]);
  }
  return true;
}

function stepValidation(item: string): boolean {
  if (step.value > 0 && item === "About You") {
    return customerProject.customer.name.length > 0 &&
      (customerProject.preferredContact === ContactMethod.Email ? customerProject.customer.email.length > 0 : customerProject.customer.phone.length > 0);
  }
  return true;
}

function sendMail() {
  if (validateStepRules(0) && validateStepRules(1)) {
    if (tellUsAboutYourProject.value) {
      mail.sendEmail(tellUsAboutYourProject.value).then((response) => {
        snackbar.value = true;
        isValid.value = response.success;

        if (response.success) {
          tellUsAboutYourProject.value?.reset();
          step.value = 0;
          Object.assign(customerProject, {
            customer: {
              name: '',
              address: '',
              city: '',
              state: '',
              zip: '',
              email: '',
              phone: '',
            },
            project: {
              description: '',
              images: [],
            },
            preferredContact: ContactMethod.Email,
          });
        } else {
          console.error(response.message);
        }
      }).catch((error) => {
        console.error("Unexpected error:", error);
        snackbar.value = true;
        isValid.value = false;
      });
    }
  } else {
    snackbar.value = true;
    isValid.value = false;
    console.warn("Step validation failed.");
  }
}
</script>

<style></style>